import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface DialogProps {
    open: boolean;
    title: string;
    description?: React.ReactNode;
    onClose: () => void;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    /** Wider sheet for tree pickers. */
    wide?: boolean;
    /** Block closing (Escape / scrim) while an operation is running. */
    busy?: boolean;
    /** Render at the document's root: for a dialog opened from inside a node card (the canvas is transformed, which would pin the scrim to the card). */
    portal?: boolean;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal sheet on the shared ns-modal-scrim/sheet styles: aria-modal, labelled,
 * Escape closes, focus moves inside on open and returns to the opener on close,
 * Tab cycles within the sheet.
 */
export const Dialog: React.FC<DialogProps> = ({ open, title, description, onClose, children, footer, wide, busy, portal }) => {
    const sheetRef = useRef<HTMLDivElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const titleId = useId();
    const descId = useId();

    useEffect(() => {
        if (!open) return;
        openerRef.current = document.activeElement as HTMLElement | null;
        const sheet = sheetRef.current;
        const initial = sheet?.querySelector<HTMLElement>('[data-autofocus]') || sheet?.querySelector<HTMLElement>(FOCUSABLE) || sheet;
        // Let the pop-in animation start before moving focus (avoids a scroll jump)
        const raf = requestAnimationFrame(() => initial?.focus());
        return () => {
            cancelAnimationFrame(raf);
            const opener = openerRef.current;
            if (opener && document.contains(opener)) requestAnimationFrame(() => opener.focus());
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !busy) {
                e.stopPropagation();
                onClose();
                return;
            }
            if (e.key === 'Tab' && sheetRef.current) {
                const nodes = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(n => n.offsetParent !== null);
                if (nodes.length === 0) return;
                const first = nodes[0];
                const last = nodes[nodes.length - 1];
                if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
                else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, busy, onClose]);

    if (!open) return null;

    const sheet = (
        <div className="ns-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} onPointerDown={(e) => e.stopPropagation()}>
            <div
                ref={sheetRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={description ? descId : undefined}
                className={`ns-modal-sheet ${wide ? 'max-w-[560px]' : ''} p-6 sm:p-8 max-h-[90vh] overflow-y-auto overflow-x-hidden`}
                tabIndex={-1}
            >
                <div className="flex items-start justify-between gap-4 mb-1">
                    <h2 id={titleId} className="ns-t-dialog min-w-0 break-words [overflow-wrap:anywhere]">{title}</h2>
                    <button
                        type="button"
                        className="ns-icon-btn -mr-2 -mt-1"
                        aria-label="Close"
                        onClick={onClose}
                        disabled={busy}
                    >
                        <X aria-hidden="true" />
                    </button>
                </div>
                {description && <p id={descId} className="text-sm text-ink-soft mb-5">{description}</p>}
                {!description && <div className="mb-4" />}
                {children}
                {footer && <div className="flex flex-wrap justify-end gap-3 mt-6">{footer}</div>}
            </div>
        </div>
    );
    return portal && typeof document !== 'undefined' ? createPortal(sheet, document.body) : sheet;
};
